import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createClient } from '@connectrpc/connect';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { CheckoutService } from 'proto/gen/ts/shirt/v1/checkout_pb';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../routes';
import { selectCartTotal, useCartStore } from '../store/cartStore';
import { formatPrice } from '../utils/format';
import { transport } from '../utils/transport';
const checkoutClient = createClient(CheckoutService, transport);
export default function CheckoutPage() {
    const items = useCartStore((s)=>s.items);
    const subtotalCents = useCartStore(selectCartTotal);
    const [step, setStep] = useState('shipping');
    const [shippingOptions, setShippingOptions] = useState([]);
    const [selectedShipping, setSelectedShipping] = useState(null);
    const [loadingQuotes, setLoadingQuotes] = useState(false);
    const [stripePromise, setStripePromise] = useState(null);
    const [error, setError] = useState(null);
    const initialized = useRef(false);
    useEffect(()=>{
        if (initialized.current) return;
        initialized.current = true;
        checkoutClient.getCheckoutConfig({}).then((res)=>{
            if (res.publishableKey) {
                setStripePromise(loadStripe(res.publishableKey));
            } else {
                setError('Stripe is not configured.');
            }
        }).catch(()=>setError('Failed to load checkout configuration.'));
    }, []);
    const fetchShippingQuotes = useCallback(async ()=>{
        if (items.length === 0) return;
        setLoadingQuotes(true);
        setError(null);
        try {
            const res = await checkoutClient.getShippingQuote({
                items: items.map((item)=>({
                        productId: item.productId,
                        quantity: item.quantity
                    })),
                countryCode: 'US'
            });
            const sorted = [
                ...res.options
            ].sort((a, b)=>Number(a.shippingCostCents) - Number(b.shippingCostCents));
            setShippingOptions(sorted);
            if (sorted.length > 0) setSelectedShipping(sorted[0]);
        } catch  {
            setError('Failed to get shipping quotes.');
        } finally{
            setLoadingQuotes(false);
        }
    }, [
        items
    ]);
    const mountedRef = useRef(false);
    useEffect(()=>{
        if (mountedRef.current) return;
        mountedRef.current = true;
        fetchShippingQuotes();
    }, [
        fetchShippingQuotes
    ]);
    const fetchClientSecret = useCallback(async ()=>{
        if (!selectedShipping) throw new Error('Missing shipping selection');
        const res = await checkoutClient.createCheckoutSession({
            items: items.map((item)=>({
                    productId: item.productId,
                    quantity: item.quantity
                })),
            returnUrl: window.location.origin + ROUTES.checkoutConfirmation,
            shippingMethod: selectedShipping.method,
            shippingCostCents: selectedShipping.shippingCostCents,
            shippingCountryCode: 'US'
        });
        return res.clientSecret;
    }, [
        items,
        selectedShipping
    ]);
    if (items.length === 0) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center",
            children: [
                /*#__PURE__*/ _jsx("h1", {
                    className: "font-heading text-2xl font-bold text-stone-900 mb-4",
                    children: "Checkout"
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-stone-500 mb-6",
                    children: "Your cart is empty."
                }),
                /*#__PURE__*/ _jsx(Link, {
                    to: ROUTES.shop,
                    className: "inline-block bg-stone-900 text-white px-6 py-3 text-xs font-semibold uppercase tracking-wider hover:bg-stone-700",
                    children: "Browse Products"
                })
            ]
        });
    }
    if (error && shippingOptions.length === 0) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center",
            children: [
                /*#__PURE__*/ _jsx("h1", {
                    className: "font-heading text-2xl font-bold text-stone-900 mb-4",
                    children: "Checkout"
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-red-600 mb-6",
                    children: error
                }),
                /*#__PURE__*/ _jsx(Link, {
                    to: ROUTES.cart,
                    className: "inline-block border-2 border-stone-900 text-stone-900 px-6 py-3 text-xs font-semibold uppercase tracking-wider",
                    children: "Back to Cart"
                })
            ]
        });
    }
    if (!stripePromise) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center animate-pulse",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "h-8 bg-stone-200 w-1/3 mx-auto mb-8"
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "h-64 bg-stone-100 w-full"
                })
            ]
        });
    }
    const shippingCents = selectedShipping ? Number(selectedShipping.shippingCostCents) : 0;
    const totalCents = subtotalCents + shippingCents;
    if (step === 'shipping') {
        return /*#__PURE__*/ _jsxs("div", {
            className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "mb-10",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "w-10 h-1 bg-stone-900 mb-4"
                        }),
                        /*#__PURE__*/ _jsx("h1", {
                            className: "font-display text-5xl text-stone-900 uppercase tracking-wide",
                            children: "Checkout"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-xs text-stone-500 mb-6 uppercase tracking-wider",
                    children: "Shipping to United States"
                }),
                loadingQuotes ? /*#__PURE__*/ _jsx("div", {
                    className: "space-y-3 animate-pulse",
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "h-16 bg-stone-100 w-full"
                    })
                }) : /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-3 mb-8",
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-sm font-semibold text-stone-900 uppercase tracking-wider mb-2",
                            children: "Shipping Method"
                        }),
                        shippingOptions.map((opt)=>/*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                onClick: ()=>setSelectedShipping(opt),
                                className: `w-full flex items-center justify-between p-4 border-2 text-left ${selectedShipping?.method === opt.method ? 'border-stone-900 bg-stone-50' : 'border-stone-200 hover:border-stone-400'}`,
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "font-semibold text-stone-900",
                                        children: opt.method
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "font-bold text-stone-900",
                                        children: Number(opt.shippingCostCents) === 0 ? 'Free' : formatPrice(opt.shippingCostCents)
                                    })
                                ]
                            }, opt.method))
                    ]
                }),
                selectedShipping && /*#__PURE__*/ _jsxs("div", {
                    className: "border-t-2 border-stone-900 pt-6 mt-8",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex justify-between text-sm text-stone-500 mb-2",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    children: "Subtotal"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    children: formatPrice(subtotalCents)
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex justify-between text-sm text-stone-500 mb-4",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    children: "Shipping"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    children: shippingCents === 0 ? 'Free' : formatPrice(shippingCents)
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex justify-between items-baseline mb-6",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-sm font-semibold text-stone-900 uppercase tracking-wider",
                                    children: "Total"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-3xl font-display text-stone-900 uppercase",
                                    children: formatPrice(totalCents)
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-col sm:flex-row gap-3",
                            children: [
                                /*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    onClick: ()=>setStep('payment'),
                                    className: "flex-1 py-4 bg-stone-900 text-white text-xs font-semibold uppercase tracking-[0.15em] hover:bg-stone-700",
                                    children: "Continue to Payment"
                                }),
                                /*#__PURE__*/ _jsx(Link, {
                                    to: ROUTES.cart,
                                    className: "py-4 px-8 text-center border-2 border-stone-900 text-stone-900 text-xs font-semibold uppercase tracking-[0.15em]",
                                    children: "Back to Cart"
                                })
                            ]
                        })
                    ]
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8",
        children: [
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                onClick: ()=>setStep('shipping'),
                className: "text-sm text-stone-500 hover:text-stone-900 underline mb-6",
                children: "Back to Shipping"
            }),
            /*#__PURE__*/ _jsx("h1", {
                className: "font-heading text-3xl font-bold text-stone-900 mb-8",
                children: "Payment"
            }),
            /*#__PURE__*/ _jsx("div", {
                id: "checkout",
                children: /*#__PURE__*/ _jsx(EmbeddedCheckoutProvider, {
                    stripe: stripePromise,
                    options: {
                        fetchClientSecret
                    },
                    children: /*#__PURE__*/ _jsx(EmbeddedCheckout, {})
                })
            })
        ]
    });
}
